import { ComponentType } from "react";

import { mapValidation } from "../../utils/mapValidation";
import {
  Control,
  FieldValues,
  RegisterOptions,
  useFormContext,
  useController,
  UnpackNestedValue,
  FieldPathValue,
} from "react-hook-form";

export interface IWithFormControl {
  control?: Control<FieldValues>;
  name: string;
  rules?: Omit<
    RegisterOptions<FieldValues>,
    "valueAsNumber" | "valueAsDate" | "setValueAs"
  >;
  validationArr?: string[];
  defaultValue?: UnpackNestedValue<FieldPathValue<FieldValues, string>>;
}

/**
 * HOC to wrap hook inputs with UseControllerReturn values
 */
const withFormControl = <T,>(
  WrappedInput: ComponentType<T>,
  emptyDefaultValue?: UnpackNestedValue<FieldPathValue<FieldValues, string>>
): ComponentType<IWithFormControl & Omit<T, "controllerFields">> => {
  const InputComponent = ({
    validationArr = [],
    rules = {},
    control,
    defaultValue: passedDefaultValue,
    ...hocProps
  }: IWithFormControl & Omit<T, "controllerFields">) => {
    const { name } = hocProps;
    const methods = useFormContext();
    const defaultValue = passedDefaultValue || emptyDefaultValue;

    const mappedValidation = mapValidation(validationArr);

    if (!control && !methods) {
      return <div>Input Missing Controller</div>;
    }

    // use either passed in control, or control from context
    const controllerFields = useController({
      name,
      control: control || methods?.control,
      rules: {
        ...rules,
        validate: {
          ...mappedValidation,
          ...rules.validate,
        },
      },
      defaultValue,
    });

    return (
      <WrappedInput
        {...(hocProps as IWithFormControl & T)} // allows for any number of inputs from the variety we have to be passed in
        controllerFields={controllerFields}
      />
    );
  };
  return InputComponent;
};

export default withFormControl;
